import { TextField } from "@mui/material";
import React, { useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import "./ContactPage.css";

function ContactPage() {
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState(false);

  const handlePhoneChange = (event) => {
    setPhone(event.target.value);
    if (event.target.value.length !== 12) {
      setPhoneError(true);
    } else {
      setPhoneError(false);
    }
  };

  const handlePhoneNumber = (event) => {
    let value = event.target.value;
    value = value.replace(/\D/g, ""); // Remove all non-digit characters
    value = value.substring(0, 10); // Limit to first 10 digits
    if (value.length > 3 && value.length <= 6)
      value = value.slice(0, 3) + "-" + value.slice(3);
    else if (value.length > 6)
      value =
        value.slice(0, 3) + "-" + value.slice(3, 6) + "-" + value.slice(6);
    setPhone(value);
  };

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState(false);

  const handleNameChange = (event) => {
    setName(event.target.value);
    if (event.target.value === "") {
      setNameError(true);
    } else {
      setNameError(false);
    }
  };

  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [trim, setTrim] = useState("");
  const [make, setMake] = useState("");
  const [message, setMessage] = useState("");

  const handleModelChange = (event) => {
    setModel(event.target.value);
  };

  const handleYearChange = (event) => {
    const re = /^[0-9\b]+$/; // Regular expression to match numbers and backspace

    // if value is not blank, then test the regex
    if (event.target.value === "" || re.test(event.target.value)) {
      setYear(event.target.value);
    }
  };

  const handleTrimChange = (event) => {
    setTrim(event.target.value);
  };

  const handleMakeChange = (event) => {
    setMake(event.target.value);
  };

  const handleMessageChange = (event) => {
    setMessage(event.target.value);
  };

  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null); // null | "success" | "error"

  const resetForm = () => {
    setName("");
    setPhone("");
    setMake("");
    setModel("");
    setYear("");
    setTrim("");
    setMessage("");
  };

  const callApi = (path, method, body) =>
    fetch(`https://api.usautoglass.net/${path}`, {
      method: method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error(`${path} returned ${response.status}`);
    });

  /* sendCustomer emails the lead (required); newCustomer saves it to DynamoDB (best effort) */
  const handleSubmit = async () => {
    const customer = {
      name: name,
      phone: phone,
      make: make,
      model: model,
      year: parseInt(year, 10),
      trim: trim,
    };

    setIsLoading(true);
    const [email, save] = await Promise.allSettled([
      callApi("sendCustomer", "POST", { ...customer, message: message }),
      callApi("newCustomer", "PUT", customer),
    ]);
    setIsLoading(false);

    if (save.status === "rejected") console.error("Error:", save.reason);
    if (email.status === "fulfilled") {
      resetForm();
      setResult("success");
    } else {
      console.error("Error:", email.reason);
      setResult("error");
    }
    setTimeout(() => setResult(null), 4000);
  };


  const canSubmit = !nameError && !phoneError && name !== "" && phone !== "";

  return (
    <main className="contact-page">
      {isLoading && (
        <div className="contact-overlay">
          <CircularProgress size={80} />
        </div>
      )}
      {result && (
        <div className="contact-overlay">
          <Alert severity={result} style={{ borderRadius: "12px" }}>
            {result === "success"
              ? "Your message has been sent successfully!"
              : "Sorry, your message couldn't be sent. Please try again."}
          </Alert>
        </div>
      )}

      <div className="contact-layout">
        <aside className="contact-info">
          <span className="eyebrow eyebrow-light">Request a Quote</span>
          <h1 className="contact-title">Tell us about your vehicle</h1>
          <p className="contact-lead">
            Provide your <strong>name</strong> and{" "}
            <strong>phone number</strong>, plus any details about your car and
            the glass damage. We'll get back to you ASAP!
          </p>
          <ul className="contact-points">
            {[
              "Windshields, side & rear windows, sunroofs",
              "Family-owned, 15+ years of experience",
              "Serving DC, Maryland & Virginia",
            ].map((point) => (
              <li key={point}>
                <CheckCircleRoundedIcon fontSize="small" />
                {point}
              </li>
            ))}
          </ul>
        </aside>

        <div className="contact-card">
          <div className="form-grid">
            <TextField
              id="nameField"
              className="span-half"
              label="Name"
              required
              error={nameError}
              helperText={nameError ? "Name is required" : ""}
              value={name}
              onChange={handleNameChange}
            />

            <TextField
              id="phoneField"
              className="span-half"
              label="Phone #"
              required
              inputProps={{
                maxLength: 12,
                pattern: "[0-9]{3}-[0-9]{3}-[0-9]{4}",
              }}
              error={phoneError}
              helperText={phoneError ? "Phone # must be 10 digits" : ""}
              value={phone}
              onChange={(event) => {
                handlePhoneChange(event);
                handlePhoneNumber(event);
              }}
            />

            <TextField id="makeField" label="Make" value={make} onChange={handleMakeChange} />
            <TextField id="modelField" label="Model" value={model} onChange={handleModelChange} />
            <TextField
              id="yearField"
              label="Year"
              value={year}
              onChange={handleYearChange}
              inputProps={{ maxLength: 4 }}
            />
            <TextField id="trimField" label="Trim" value={trim} onChange={handleTrimChange} />

            <TextField
              id="messageField"
              className="span-full"
              label="Message"
              placeholder="Describe the glass damage (location, size, chip or crack...)"
              multiline
              rows={4}
              value={message}
              onChange={handleMessageChange}
            />
          </div>

          <Button
            className="submit-button"
            variant="contained"
            size="large"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {canSubmit ? "Submit Request" : "Enter your name and phone number"}
          </Button>
        </div>
      </div>
    </main>
  );
}

export default ContactPage;
